var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useEffect, useRef, useState } from 'react';
import { IconButton } from '../IconButton/IconButton';
import { SuperEllipse } from '../SuperEllipse/SuperEllipse';
import { Typography } from '../Typography/Typography';
import { toaster, toasterContent } from './styles';
const variantProperties = {
    success: {
        colorSet: 'success',
        icon: 'check',
    },
    error: {
        colorSet: 'critical',
        icon: 'cancel',
    },
};
const getToasterA11yProps = (variant) => {
    if (variant === 'error') {
        return {
            role: 'alert',
        };
    }
    return {
        role: 'status',
        'aria-live': 'polite',
        'aria-atomic': true,
    };
};
export const Toaster = (_a) => {
    var _b;
    var { children, title, secondaryText, autoHide = true, autoHideDuration, hasCloseAction = true, open, positionOptions = {
        bottom: '0px',
        right: '0px',
    }, onClose, variant, icon, actionLabels, className } = _a, props = __rest(_a, ["children", "title", "secondaryText", "autoHide", "autoHideDuration", "hasCloseAction", "open", "positionOptions", "onClose", "variant", "icon", "actionLabels", "className"]);
    const [visible, setVisibility] = useState(open);
    const visibilityTimer = useRef(null);
    const toasterRef = useRef(null);
    const clearVisibilityTimer = () => {
        if (visibilityTimer.current) {
            clearInterval(visibilityTimer.current);
        }
    };
    const closeToaster = useCallback(() => {
        onClose === null || onClose === void 0 ? void 0 : onClose();
        setVisibility(false);
    }, [onClose]);
    useEffect(() => {
        clearVisibilityTimer();
        if (open && autoHide && autoHideDuration) {
            visibilityTimer.current = setInterval(closeToaster, autoHideDuration);
        }
        setVisibility(open);
        return () => {
            clearVisibilityTimer();
        };
    }, [open, autoHide, autoHideDuration, onClose, closeToaster]);
    const a11yProps = getToasterA11yProps(variant);
    return (_jsxs("div", Object.assign({}, props, { tabIndex: -1, ref: toasterRef, className: toaster({ opened: visible, className }), style: Object.assign(Object.assign({}, (positionOptions && {
            '--bottom': positionOptions.bottom,
            '--right': positionOptions.right,
            '--top': positionOptions.top,
            '--left': positionOptions.left,
        })), { '--z-index': (_b = props.zIndex) !== null && _b !== void 0 ? _b : '200' }) }, a11yProps, { children: [children || (_jsxs(_Fragment, { children: [variant && (_jsx(SuperEllipse, { size: secondaryText ? 48 : 24, mr: 12, icon: icon || variantProperties[variant].icon, colorSet: variantProperties[variant].colorSet })), _jsxs("div", { className: toasterContent({
                            className: [hasCloseAction && 'mr-8'],
                        }), children: [title && (_jsx(_Fragment, { children: typeof title === 'string' ? (_jsx(Typography, { variant: "body", weight: "medium", title: title, children: title })) : (_jsx("span", { children: title })) })), secondaryText && (_jsx(_Fragment, { children: typeof secondaryText === 'string' ? (_jsx(Typography, { variant: "body", title: secondaryText, children: secondaryText })) : (_jsx("span", { children: secondaryText })) }))] })] })), hasCloseAction && (_jsx(IconButton, { size: 24, name: "cancel", variant: "secondary", onClick: closeToaster, "aria-label": actionLabels === null || actionLabels === void 0 ? void 0 : actionLabels.close }))] })));
};
